import { Component } from "react"

/* Components */
import { Cards, Notes, InnerLayout } from "../../components"

/* Widgets */
import RoundStat from "./RoundStat"
import RoundPlayers from "./RoundPlayers"
import RoundPlayer from "./RoundPlayer"
import RoundRefunds from "./RoundRefunds"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* Helpers */
import { utils } from "../../helpers"

/* Toast */
import { pushToast } from "../../components/Toast"
import { pushConfirm } from "../../components/Confirm"

/* Icons */
import {
    SpadeIcon, HashIcon, TagIcon, ClockIcon, DealerIcon, FlagIcon, PauseIcon, EditIcon,
    PlayersIcon, ReceiptIcon, DebitIcon, CreditIcon, NetIcon, RefundIcon,
    TrophyIcon, StopIcon, PlayIcon, RotateIcon, WarningIcon, CardsIcon, ArrowIcon, PayIcon, EyeOffIcon,
} from "../../components/Icons"


/* THE TONE OF EVERY WINNER A ROUND CAN HAVE */
const WINNERS = {
    player: "blue",
    banker: "red",
    tie: "green",
}


/*
    Widget RoundView — THE CARD OF ONE ROUND, THE SAME ONE IN EVERY LIVE GAME

    The concept it draws is the one the panel always had: what the round is, what it did to the money,
    what the admin can still do to it, the cards it was played with and the players who sat at it.
    Only the order changed — the cards of the round now come before its players.

    api      the module of the game — game, players, view, credit, creditAll, end, togglePause
    config   what the game draws of its own, see the fields read below
*/
class RoundView extends Component {

    constructor() {
        super()

        this.state = {
            table: null,
            data: null,
            cards: [],
            status: "loading",

            /* The Players Of The Round */
            players: [],
            pstatus: "loading",
            count: 0,
            page: 1,
            number: "",
        }
    }

    componentDidMount = () => {
        this.load()
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { api, uid, gameID, postfix } = this.props

        /* Check Parameters */
        if (!uid || !gameID) {
            this.setState({ status: "error", data: null })
            return
        }

        /* Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        api.game({ uid, gameID, postfix }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.game,
                    table: response.data.table,
                    cards: response.data.cards ? response.data.cards : [],
                })
                this.getPlayers()
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: null, table: null, cards: [] })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: null, table: null, cards: [] })
            }
            else {
                this.setState({ status: "error", data: null, table: null, cards: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", data: null, table: null, cards: [] })
        })
    }

    /* Load The Players Of The Round */
    getPlayers = (page = 1, reload = false) => {

        /* Fields */
        const { api, uid, gameID, postfix } = this.props
        const { number } = this.state

        /* Check Parameters */
        if (!uid || !gameID) {
            this.setState({ pstatus: "error", players: [] })
            return
        }

        /* Save Page */
        this.setState({ page })

        /* Reload */
        if (reload) {
            this.setState({ pstatus: "loading" })
        }

        /* Send to REST API */
        api.players({ uid, gameID, page, number, postfix }).then((response) => {
            if (response.status === 200) {
                this.setState({ pstatus: "ok", players: response.data.list, count: response.data.count })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ pstatus: "permission", players: [], count: 0 })
            }
            else if (response.status === 498) {
                this.setState({ pstatus: "key", players: [], count: 0 })
            }
            else {
                this.setState({ pstatus: "error", players: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ pstatus: "network", players: [], count: 0 })
        })
    }

    /* Set The Code The Players Are Searched By — an empty field brings every one of them back */
    setNumber = (number) => {
        if (`${number}`.trim() === "") {
            this.setState({ number: "" }, () => this.getPlayers(1, true))
        }
        else {
            this.setState({ number })
        }
    }

    /* ARE THE CARDS OF THE ROUND SHOWN — ONLY A FINISHED OR A PAUSED ROUND OPENS ITS HAND */
    isCardsVisible = () => {

        /* Fields */
        const { data } = this.state

        /* Check Round */
        if (!data) {
            return false
        }

        /* The Round Is Finished Or It Stands On Pause */
        return parseInt(data.status) === 1 || parseInt(data.isPaused) === 1
    }

    /* Get The Cards Of One Row */
    getCards = (type, box = null) => {

        /* Fields */
        const { cards } = this.state
        const list = []

        /* Check Cards */
        if (cards && Array.isArray(cards) && cards.length > 0) {
            cards.forEach((card) => {
                if (box !== null) {
                    if (card.type === type && parseInt(card.box) === parseInt(box)) {
                        list.push(card)
                    }
                }
                else if (card.type === type) {
                    list.push(card)
                }
            })
        }

        return list
    }

    /* THE FAILED AND THE HANGING TRANSACTIONS OF THE ROUND */
    getProblem = () => {

        /* Fields */
        const { data } = this.state

        /* Fields */
        const failed = parseInt(data.failed) > 0 ? parseInt(data.failed) : 0
        const pending = parseInt(data.pending) > 0 ? parseInt(data.pending) : 0

        /* Check Counters */
        if (failed === 0 && pending === 0) {
            return null
        }

        /* A Failure Outweighs A Hanging Request */
        return {
            tone: failed > 0 ? "failed" : "pending",
            count: failed + pending,
            hint: [
                failed > 0 ? `${t("gtx.failed")}: ${failed}` : "",
                pending > 0 ? `${t("gtx.pending")}: ${pending}` : "",
            ].filter((part) => part).join(" · "),
        }
    }

    /* Open The Refunds Of The Round */
    openRefunds = () => {

        /* Fields */
        const { uid } = this.props
        const { data } = this.state

        /* Check Modal */
        if (!this._refunds || !data) {
            return
        }

        this._refunds.open({ uid, round: data.roundId })
    }

    /* Open One Player Of The Round */
    openPlayer = (item) => {

        /* Check Modal */
        if (!this._player) {
            return
        }

        this._player.open({ player: item.player })
    }


    /* END ACTION — THE ROUND IS CLOSED BY HAND, WITH OR WITHOUT GIVING THE BETS BACK */
    end = (refund = false) => {

        /* Fields */
        const { api, uid, gameID, socket, postfix } = this.props
        const { data } = this.state

        /* Check Parameters */
        if (!uid || !gameID) {
            pushToast({ text: t("widget.unknownError"), type: "error" })
            return
        }

        /* Check Status */
        if (parseInt(data.status) === 1) {
            return
        }

        /* Check Paused */
        if (parseInt(data.isPaused) === 1) {
            pushToast({ text: t("widget.gamePaused"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: t("widget.finishConfirmAlt"),
            text: t("widget.finishWarningAlt"),
            icon: "warning",
            danger: true,
            confirmText: t("widget.finishConfirmBtn"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Send to REST API */
            api.end({ uid, gameID, refund, postfix }).then((response) => {
                if (response.status === 200) {

                    /* Notify Every Player Of The Round */
                    if (response.data.update === "yes" && socket) {
                        socket.emit("end", { refund, gameID })
                    }

                    this.load(true)
                    pushToast({ text: t("widget.finishDone"), type: "success" })
                }
                else if (response.status === 401 || response.status === 403) {
                    pushToast({ text: t("swal.noAccess"), type: "error" })
                }
                else if (response.status === 498) {
                    pushToast({ text: t("swal.invalidKey"), type: "error" })
                }
                else {
                    pushToast({ text: t("swal.generic"), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("swal.generic"), type: "error" })
            })

        })

    }

    /* PAUSE ACTION */
    pause = () => {

        /* Fields */
        const { api, uid, socket, postfix, config } = this.props
        const { data } = this.state

        /* Check Status */
        if (parseInt(data.status) === 1) {
            pushToast({ text: t("widget.gameFinished"), type: "error" })
            return
        }

        /* Check Paused */
        if (parseInt(data.isPaused) === 1) {
            pushToast({ text: t("widget.alreadyPaused"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: t("widget.pauseConfirm"),
            text: t("widget.pauseWarning"),
            icon: "warning",
            confirmText: t("widget.pauseButton"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Send to REST API */
            api.togglePause({ uid, gameID: data.id, type: "pause", postfix }).then((response) => {
                if (response.status === 200) {

                    if (response.data.update === "yes") {

                        /* Notify The Dealer — a part of the games waits for the round inside the event */
                        if (socket) {
                            socket.emit("pause", config && config.pauseGameID ? { from: "support", gameID: data.id } : { from: "support" })
                        }

                        pushToast({ text: t("widget.gamePausedSuccess"), type: "success" })
                    }
                    else {
                        pushToast({ text: t("widget.warningStatusDb"), type: "warning" })
                    }

                    this.load(true)
                }
                else {
                    pushToast({ text: t("widget.somethingWrong"), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("widget.somethingWrong"), type: "error" })
            })

        })

    }

    /* CONTINUE ACTION — THE ROUND GOES ON, EITHER AS IT WAS OR WITH THE CARDS SCANNED AGAIN */
    continue = (revoke = false) => {

        /* Fields */
        const { api, uid, socket, postfix, config } = this.props
        const { data } = this.state

        /* Check Status — a round that finished while standing on pause still holds the table,
           so the games that allow it may continue it to let the next round start */
        if (parseInt(data.status) === 1 && !(config && config.continueFinished && parseInt(data.isPaused) === 1)) {
            return
        }

        /* Check Paused */
        if (parseInt(data.isPaused) === 0) {
            pushToast({ text: t("widget.gameNotPaused"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: t("widget.continueConfirm"),
            text: revoke ? t("widget.resetCardsText") : t("widget.continueText"),
            icon: "warning",
            confirmText: revoke ? t("widget.resetCards") : (config && config.continueText ? config.continueText() : t("widget.continueWithout")),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Send to REST API */
            api.togglePause({ uid, gameID: data.id, type: "continue", postfix }).then((response) => {
                if (response.status === 200) {

                    if (response.data.update === "yes") {

                        /* Notify The Dealer */
                        if (socket) {
                            socket.emit("continue", { type: revoke ? "revoke" : "continue", gameID: data.id })
                        }

                        pushToast({ text: revoke ? t("widget.cardsReset") : t("widget.gameContinued"), type: "success" })
                    }
                    else {
                        pushToast({ text: t("widget.warningStatusDb"), type: "warning" })
                    }

                    this.load(true)
                }
                else {
                    pushToast({ text: t("widget.somethingWrong"), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("widget.somethingWrong"), type: "error" })
            })

        })

    }

    /* CREDIT ALL — EVERY BET OF THE ROUND GOES BACK, AND FOR SOME GAMES THE WINS GO OUT AS WELL */
    creditAll = (pay = false) => {

        /* Fields */
        const { api, uid, socket, postfix } = this.props
        const { data } = this.state

        /* Fields */
        const call = pay ? api.creditAndDebitAll : api.creditAll

        /* Check Call */
        if (!call) {
            return
        }

        /* Ask */
        pushConfirm({
            title: pay ? t("widget.refundAndPayAllConfirm") : t("widget.refundAllConfirm"),
            text: t("widget.refundAllWarning"),
            icon: "warning",
            danger: true,
            confirmText: pay ? t("widget.refundAllBetsAndPay") : t("widget.refundAllBtn"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Send to REST API */
            call({ uid, gameID: data.id, postfix }).then((response) => {
                if (response.status === 200) {

                    /* Notify The Players Whose Money Went Back */
                    if (socket) {
                        socket.emit("refunds", response.data.refunds)
                    }

                    pushToast({ text: pay ? t("widget.allRefundsComplete") : t("widget.refundsComplete"), type: "success" })
                    this.load(true)
                }
                else {
                    pushToast({ text: t("widget.somethingWrong"), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("widget.somethingWrong"), type: "error" })
            })

        })

    }


    /* Draw The Head Of The Round */
    _hero = () => {

        /* Fields */
        const { config } = this.props
        const { data, table } = this.state

        /* Fields */
        const datetime = utils.getTime(data.createdAt)
        const problem = this.getProblem()
        const ggr = parseFloat(data.ggr)

        return (
            <div className="rnd--hero">

                <div className="rnd--hero-main">

                    <div className="rnd--hero-round">#{data.number}</div>

                    <div className="rnd--hero-text">
                        <h3>{table.name}</h3>
                        <p><ClockIcon size={11} />{datetime.time}, {datetime.date}</p>
                    </div>

                </div>

                <div className="rnd--hero-badges">

                    {parseInt(data.status) === 1
                        ? <div className="rnd--badge blue">{t("widget.completed")}</div>
                        : <div className="rnd--badge violet">{t("widget.notCompleted")}</div>
                    }

                    {parseInt(data.isPaused) === 1 && <div className="rnd--badge red"><PauseIcon size={11} />{t("widget.paused")}</div>}

                    {parseInt(data.isUpdated) === 1 && <div className="rnd--badge pink"><EditIcon size={11} />{t("widget.changed")}</div>}

                    {/* The Number The Wheel Landed On */}
                    {config && config.win === "roulette" && data.win
                        ? <div className={`rnd--wheel ${data.color ? data.color : "nospin"}`}>{data.win}</div>
                        : null
                    }

                    {/* The Side That Took The Round */}
                    {config && config.win === "punto" && data.win
                        ? <div className={`rnd--badge ${WINNERS[data.win] ? WINNERS[data.win] : ""}`}><TrophyIcon size={11} />{`${data.win}`.toUpperCase()}</div>
                        : null
                    }

                    {/* The Money Of The Round Never Went Through */}
                    {problem &&
                        <div className={`rnd--badge ${problem.tone}`} title={problem.hint}>
                            <WarningIcon size={11} />
                            {t("round.problem")} {problem.count}
                        </div>
                    }

                </div>

                <div className="rnd--hero-money">

                    <div className="rnd--hero-money-row">
                        <span>CREDIT</span>
                        <p>{utils.convertor(data.credit, "$")}</p>
                    </div>

                    <div className="rnd--hero-money-row">
                        <span>DEBIT</span>
                        <p>{utils.convertor(data.debit, "$")}</p>
                    </div>

                    <div className={`rnd--hero-net ${ggr > 0 ? "green" : (ggr < 0 ? "red" : "")}`}>
                        <span>GGR</span>
                        <p>{ggr > 0 ? "+ " : (ggr < 0 ? "- " : "")}{utils.convertor(Math.abs(ggr), "$")}</p>
                    </div>

                </div>

            </div>
        )
    }

    /* Draw What The Round Is */
    _about = () => {

        /* Fields */
        const { data, table } = this.state

        /* Fields */
        const datetime = utils.getTime(data.createdAt)

        return (
            <div className="rnd--card">

                <div className="rnd--card-head"><SpadeIcon size={13} />{t("round.about")}</div>

                <div className="rnd--rows">

                    <RoundStat icon={<SpadeIcon size={14} />} title={t("widget.game")}>{table.name}</RoundStat>

                    <RoundStat icon={<HashIcon size={14} />} title={t("widget.gameNumber2")}>{data.number}</RoundStat>

                    <RoundStat icon={<TagIcon size={14} />} tone="blue" title={t("widget.roundId")}>
                        <span className="rnd--mono">{utils.checkValue(data.roundId, "—")}</span>
                    </RoundStat>

                    <RoundStat icon={<ClockIcon size={14} />} title={t("widget.date")}>
                        {datetime.time}, <span className="rnd--muted">{datetime.date}</span>
                    </RoundStat>

                    <RoundStat icon={<DealerIcon size={14} />} title={t("widget.dealer")}>{utils.checkValue(data.dealer, "—")}</RoundStat>

                    <RoundStat icon={<FlagIcon size={14} />} title={t("widget.gameStatus")}>
                        {parseInt(data.status) === 1
                            ? <div className="rnd--badge blue">{t("widget.completed")}</div>
                            : <div className="rnd--badge violet">{t("widget.notCompleted")}</div>
                        }
                    </RoundStat>

                </div>

            </div>
        )
    }

    /* Draw What The Round Did To The Money */
    _money = () => {

        /* Fields */
        const { data } = this.state

        /* Fields */
        const ggr = parseFloat(data.ggr)

        return (
            <div className="rnd--card">

                <div className="rnd--card-head"><ReceiptIcon size={13} />{t("round.money")}</div>

                <div className="rnd--rows">

                    <RoundStat icon={<PlayersIcon size={14} />} tone="blue" title={t("widget.playersCount")}>
                        {parseInt(data.players) > 0 ? parseInt(data.players) : 0}
                    </RoundStat>

                    <RoundStat icon={<ReceiptIcon size={14} />} title={t("widget.transactionCount")}>
                        {parseInt(data.transactions) > 0 ? parseInt(data.transactions) : 0}
                    </RoundStat>

                    <RoundStat icon={<DebitIcon size={14} />} tone="green" title="DEBIT">{utils.convertor(data.debit, "$")}</RoundStat>

                    <RoundStat icon={<CreditIcon size={14} />} tone="red" title="CREDIT">{utils.convertor(data.credit, "$")}</RoundStat>

                    <RoundStat icon={<NetIcon size={14} />} tone="violet" title="GGR">
                        <span className={ggr > 0 ? "green" : (ggr < 0 ? "red" : "")}>
                            {ggr > 0 ? "+ " : (ggr < 0 ? "- " : "")}{utils.convertor(Math.abs(ggr), "$")}
                        </span>
                    </RoundStat>

                    {/* Every Operation Of The Round, The Refunds Of The Admins First.
                        The operations of a round are keyed by its round id — an old round without one carries none */}
                    {`${utils.checkValue(data.roundId)}`.trim() === ""
                        ? <div className="rnd--link disabled" title={t("round.noRoundId")}>
                            <RefundIcon size={13} />
                            <span>{t("widget.allRefunds")}</span>
                        </div>
                        : <div onClick={() => this.openRefunds()} className="rnd--link">
                            <RefundIcon size={13} />
                            <span>{t("widget.allRefunds")}</span>
                            <ArrowIcon size={12} />
                        </div>
                    }

                </div>

            </div>
        )
    }

    /* Draw What The Admin Can Still Do */
    _actions = () => {

        /* Fields */
        const { config } = this.props
        const { data } = this.state

        /* Fields */
        const actions = config && config.actions ? config.actions : {}
        const done = parseInt(data.status) === 1
        const paused = parseInt(data.isPaused) === 1

        return (
            <div className="rnd--card">

                <div className="rnd--card-head"><FlagIcon size={13} />{t("round.actions")}</div>

                <div className="rnd--groups">

                    <div className="rnd--group">

                        <div className="rnd--group-title">{t("round.gameActions")}</div>

                        <div className="rnd--buttons">

                            {actions.end &&
                                <div onClick={() => this.end()} className={`rnd--button danger ${done ? "disabled" : ""}`}>
                                    <StopIcon size={13} />{t("widget.finishGame")}
                                </div>
                            }

                            {actions.endRefund &&
                                <div onClick={() => this.end(true)} className={`rnd--button ghost ${done ? "disabled" : ""}`}>
                                    <RefundIcon size={13} />{t("widget.finishAndRefund")}
                                </div>
                            }

                            {actions.refundAll &&
                                <div onClick={() => this.creditAll()} className="rnd--button ghost">
                                    <RefundIcon size={13} />{t("widget.refundAllBets")}
                                </div>
                            }

                            {actions.refundAndPayAll &&
                                <div onClick={() => this.creditAll(true)} className="rnd--button dark">
                                    <PayIcon size={13} />{t("widget.refundAllBetsAndPay")}
                                </div>
                            }

                        </div>

                    </div>

                    <div className="rnd--group">

                        <div className="rnd--group-title">{t("round.pauseActions")}</div>

                        <div className="rnd--buttons">

                            {actions.pause &&
                                <div onClick={() => this.pause()} className={`rnd--button blue ${paused ? "disabled" : ""}`}>
                                    <PauseIcon size={13} />{t("widget.pauseGame")}
                                </div>
                            }

                            {actions.revoke &&
                                <div onClick={() => this.continue(true)} className={`rnd--button indigo ${paused ? "" : "disabled"}`}>
                                    <RotateIcon size={13} />{t("widget.resetCards")}
                                </div>
                            }

                            {actions.continue &&
                                <div onClick={() => this.continue()} className={`rnd--button green ${paused ? "" : "disabled"}`}>
                                    <PlayIcon size={13} />
                                    {config && config.continueText ? config.continueText() : t("widget.continueWithout")}
                                </div>
                            }

                        </div>

                    </div>

                </div>

            </div>
        )
    }

    /* Draw The Cards Of The Round */
    _cards = () => {

        /* Fields */
        const { config } = this.props

        /* Fields */
        const rows = config && Array.isArray(config.cards) ? config.cards : []

        /* Check Rows */
        if (rows.length === 0) {
            return null
        }

        /* THE HAND OF A LIVE ROUND STAYS CLOSED — the cards come with the end of the round or with its pause */
        if (!this.isCardsVisible()) {
            return (
                <div className="rnd--cards">

                    <div className="rnd--card-head"><CardsIcon size={13} />{t("round.cards")}</div>

                    <div className="rnd--cards-hidden"><EyeOffIcon size={16} />{t("round.cardsHidden")}</div>

                </div>
            )
        }

        /* The rows a game only deals in a part of its rounds */
        const list = rows.filter((row) => {

            /* Check Row */
            if (!row.optional) {
                return true
            }

            return this.getCards(row.type, row.box === undefined ? null : row.box).length > 0
        })

        /* Check List */
        if (list.length === 0) {
            return null
        }

        return (
            <div className="rnd--cards">

                <div className="rnd--card-head"><CardsIcon size={13} />{t("round.cards")}</div>

                <div className={`cards--container ${config.cardsClass ? config.cardsClass : ""}`}>
                    {list.map((row, index) =>
                        <Cards key={`${index}`} title={row.title()} cards={this.getCards(row.type, row.box === undefined ? null : row.box)} />
                    )}
                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { api, config, uid, gameID, postfix, socket } = this.props
        const { status, data, table, players, pstatus, page, count, number } = this.state

        /* Layout */
        if (status !== "ok" || !data || !table) {
            return <InnerLayout status={status === "ok" ? "error" : status} reload={() => this.load(true)} />
        }

        return (
            <div className="rnd">

                {/* Draw Hero */}
                {this._hero()}

                {/* Draw Round */}
                <div className="rnd--grid">
                    {this._about()}
                    {this._money()}
                    {this._actions()}
                </div>

                {/* Draw Cards — the cards of the round come before its players */}
                {this._cards()}

                {/* Draw Players */}
                <RoundPlayers
                    status={pstatus}
                    data={players}
                    page={page}
                    count={count}
                    number={number}
                    onNumber={(value) => this.setNumber(value)}
                    onSearch={() => this.getPlayers(1, true)}
                    onPage={(value) => this.getPlayers(value, true)}
                    onOpen={(item) => this.openPlayer(item)}
                    reload={() => this.getPlayers(1, true)}
                />

                {/* Draw Notes */}
                <Notes uid={uid} gameID={gameID} />

                {/* Draw Player — the hand of a live round stays closed inside the player as well */}
                <RoundPlayer
                    ref={(ref) => this._player = ref}
                    api={api}
                    config={config}
                    uid={uid}
                    gameID={gameID}
                    postfix={postfix}
                    socket={socket}
                    cardsHidden={!this.isCardsVisible()}
                />

                {/* Draw Refunds */}
                <RoundRefunds ref={(ref) => this._refunds = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(RoundView)
